/**
 * Splits an array into chunks of the given size.
 */
export function chunkArray<T>(arr: T[], size: number): T[][] {
  const chunks: T[][] = [];
  for (let i = 0; i < arr.length; i += size) {
    chunks.push(arr.slice(i, i + size));
  }
  return chunks;
}

/**
 * Turns a sales point name into a deterministic, Firestore-safe document id
 * (lowercase, Turkish characters folded, non-alphanumerics collapsed to '-').
 * Deterministic so re-seeding the same name never creates a duplicate doc.
 */
export function slugifyStoreName(name: string): string {
  const folded = String(name || '')
    .trim()
    .replace(/İ/g, 'i').replace(/I/g, 'i').replace(/ı/g, 'i')
    .replace(/Ş/g, 's').replace(/ş/g, 's')
    .replace(/Ğ/g, 'g').replace(/ğ/g, 'g')
    .replace(/Ü/g, 'u').replace(/ü/g, 'u')
    .replace(/Ö/g, 'o').replace(/ö/g, 'o')
    .replace(/Ç/g, 'c').replace(/ç/g, 'c')
    .toLowerCase();
  return folded.replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'satis-noktasi';
}

/**
 * Formats a raw phone number string into Turkish phone number format: 0XXX XXX XX XX
 */
export function formatPhoneNumber(value: string): string {
  const digits = value.replace(/\D/g, '');
  if (digits.length === 0) return '';

  let cleaned = digits;
  if (cleaned.length > 0 && cleaned[0] !== '0') {
    cleaned = '0' + cleaned;
  }

  cleaned = cleaned.substring(0, 11);

  const part1 = cleaned.substring(0, 4);
  const part2 = cleaned.substring(4, 7);
  const part3 = cleaned.substring(7, 9);
  const part4 = cleaned.substring(9, 11);

  if (cleaned.length <= 4) {
    return part1;
  } else if (cleaned.length <= 7) {
    return `${part1} ${part2}`;
  } else if (cleaned.length <= 9) {
    return `${part1} ${part2} ${part3}`;
  } else {
    return `${part1} ${part2} ${part3} ${part4}`;
  }
}

/**
 * Formats a Turkish date string (YYYY-MM-DD) to "D MonthName YYYY" format in Turkish.
 */
export function formatDateTurkish(dateStr: string): string {
  if (!dateStr) return '-';
  const parts = dateStr.split('-');
  if (parts.length !== 3) return dateStr;
  const year = parts[0];
  const monthIndex = parseInt(parts[1], 10) - 1;
  const day = parseInt(parts[2], 10).toString();
  const months = [
    'Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran',
    'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'
  ];
  const monthName = months[monthIndex];
  if (!monthName) return dateStr;
  return `${day} ${monthName} ${year}`;
}

/**
 * Formats a date string (YYYY-MM-DD) to numeric "GG.AA.YYYY" format.
 */
export function formatDateNumeric(dateStr: string): string {
  const parts = dateStr.split('-');
  if (parts.length !== 3) return '';
  const [year, month, day] = parts;
  if (!year || !month || !day) return '';
  return `${day.padStart(2, '0')}.${month.padStart(2, '0')}.${year}`;
}

/**
 * Formats a month string (YYYY-MM) to "MonthName YYYY" format in Turkish, e.g. "Ocak 2027".
 */
export function formatMonthYearTurkish(monthStr: string): string {
  const parts = monthStr.split('-');
  if (parts.length !== 2) return '';
  const year = parts[0];
  const monthIndex = parseInt(parts[1], 10) - 1;
  const months = [
    'Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran',
    'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'
  ];
  const monthName = months[monthIndex];
  if (!monthName || !year) return '';
  return `${monthName} ${year}`;
}
